import React, {Component} from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/compat/app';
import {DndProvider} from 'react-dnd-multi-backend';
import {HTML5toTouch} from 'rdndmb-html5-to-touch';
import dayjs from 'dayjs';
import appConfig from 'config/app.config';
import {getPlayerModulesData} from 'helpers/module-helper';
import {playerDataTemplate} from 'data/templates/player-data-template';
import {modulesData} from 'data/modules-data';
import {backgroundsData} from 'data/backgrounds-data';
import {languagesData} from 'data/languages-data';
import ImageLoader from 'components/ui/image-loader/image-loader';
import Game from 'components/game/game';

class PlayerController extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isLoading: true,
			backgroundId: 'default',
			backgroundModifier: null,
			backgroundStatus: 'show',
			shakeScreen: false,
			bgLanguageId: this.props.languageId,
			playerData: null,
		};
		this.unsubscribePlayerData = null;
		this.timeout = null;
	}

	/**
	 * Component did mount
	 */
	componentDidMount = () => {
		/* Get user id from url */
		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);
		const userId = urlParams.get('userId');
		
		if (userId) {
			/* Check if player with that user id exists */
			const db = firebase.firestore();
			db.collection(appConfig.playersDbName).where('userId', '==', userId).get().then((docs) => {
				let playerDoc = null;
				docs.forEach((doc) => {
					if (playerDoc) return;
					if (doc) playerDoc = doc;
				});
				if (playerDoc) {
					/* Player exists, subscribe to player data */
					this.subscribeToPlayerData(playerDoc.id).then(() => {
						this.setState({isLoading: false});
					});
				} else {
					/* Player does not exist, create player */
					const newPlayerObj = JSON.parse(JSON.stringify(playerDataTemplate));
					newPlayerObj.userId = userId;
					db.collection(appConfig.playersDbName).add(newPlayerObj).then((docRef) => {
						/* Subscribe to player data */
						this.subscribeToPlayerData(docRef.id).then(() => {
							this.setState({isLoading: false});
						});
					}).catch((error) => {
						/* Error: could not create new user */
						console.error(error);
						this.setState({isLoading: false});
					});
				}
			}).catch((error) => {
				/* Error: could not connect to database */
				console.error(error);
				this.setState({isLoading: false});
			});
		} else {
			/* No user id in url params */
			this.setState({isLoading: false});
		}
	};

	/**
	 * Subscribe to player data
	 * @returns 
	 */
	subscribeToPlayerData = (playerId) => {
		return new Promise((resolve) => {
			const db = firebase.firestore();
			if (this.unsubscribePlayerData !== null) this.unsubscribePlayerData();
			this.unsubscribePlayerData =
				db.collection(appConfig.playersDbName).doc(playerId).onSnapshot((querySnapshot) => {
					if (querySnapshot.exists) {
						const playerData = {id: querySnapshot.id, ...querySnapshot.data()};
						this.setState({playerData});
						resolve({status: 'success'});
					} else {
						resolve({status: 'error'});
					}
					
				}, (error) => {
					resolve({status: 'error', error: error});
				});
		});
	};
	
	/**
	 * Update player data
	 * @param {object} updates 
	 * @returns 
	 */
	updatePlayerData = (updates) => {
		/* Nothing to update */
		if (!this.state.playerData || (Object.keys(updates).length === 0 && updates.constructor === Object)) {
			return new Promise((resolve)=>{resolve();});
		}

		return new Promise((resolve) => {
			const db = firebase.firestore();
			db.collection(appConfig.playersDbName).doc(this.state.playerData.id).update(updates).then(() => {
				resolve({status: 'success'});
			}).catch((error) => {
				resolve({status: 'error', error});
			});
		});
	};


	/**
	 * Reset player data
	 */
	resetPlayerData = () => {
		/* Reset data in database */
		this.updatePlayerData({modules: [], completed: null});
	};

	/**
	 * Start new session in module
	 * @param {string} moduleId 
	 */
	startNewSessionInModule = (moduleId) => {
		return new Promise((resolve) => {
		/* Get player modules data (all modules) */
			let playerModulesData = getPlayerModulesData(this.state.playerData);

			/* Get player data for this module */
			let moduleIndex = playerModulesData.findIndex((m) => {return m.moduleId === moduleId;});

			if (moduleIndex >= 0 && playerModulesData[moduleIndex].sessions) {
				const moduleData = modulesData.find((m) => {return m.id === moduleId;});
				const taskId = (moduleData.tasks && moduleData.tasks.length > 0 ? moduleData.tasks[0].id : null);
				const sessionTemplate = {
					isCompleted: false,
					started: dayjs(new Date()).format('YYYY-MM-DD'),
					currentTaskId:  taskId,
					points: 0,
					tasks: []
				};
				playerModulesData[moduleIndex].sessions.push(sessionTemplate);
				this.updatePlayerData({modules: playerModulesData}).then((response) => {
					resolve(response);
				});
			} else {
				resolve({status: 'error'});
			}
			

		});

	};

	/**
	 * Choose language
	 * @param {string} languageId 
	 */
	handleChooseLanguage = (languageId) => {
		this.updatePlayerData({languageId}).then(() => {
			if (this.state.page !== 'game') this.setState({page: 'game'});
		});
	};

	/**
	 * Switch background
	 * @param {string} languageId 
	 * @param {string} backgroundId 
	 * @param {string} backgroundModifier 
	 */
	setBackground = (languageId, backgroundId, backgroundModifier = null) => {
		if (
			backgroundId !== this.state.backgroundId || 
			backgroundModifier !== this.state.backgroundModifier
		) {
		/* Get background data */
			const backgroundData = backgroundsData.find((b) => {return b.id === backgroundId;});
			if (backgroundData) {
			/* Check position id */
				let newBackgroundModifier = backgroundModifier;
				if (
					backgroundData.modifiers.length > 0 &&
				(
					!backgroundModifier || 
					!backgroundData.modifiers.some((p) => {return p.id === newBackgroundModifier;})
				)
				) {
				/* Position required, but no position id or invalid position id provided */
					newBackgroundModifier = backgroundData.modifiers[0].id;
				}

				if (
					backgroundId !== this.state.backgroundId ||
				newBackgroundModifier !== this.state.backgroundModifier
				) {
					let backgroundStatus = 
					(backgroundId === this.state.backgroundId ? 'pan' : 'fade');
					if (
						(this.state.backgroundModifier && this.state.backgroundModifier.includes('zoom')) || 
					(newBackgroundModifier && newBackgroundModifier.includes('zoom'))
					) {
						backgroundStatus = 'zoom';
					}
					this.setState({
						bgLanguageId: languageId,
						backgroundId: backgroundId,
						backgroundModifier: newBackgroundModifier,
						backgroundStatus: backgroundStatus
					});
				}
			}
		}
	};

	/**
 * Update background status (show, pan, fade, zoom)
 * @param {string} status 
 */
	updateBackgroundStatus = (status) => {
		this.setState({backgroundStatus: status});
	};

	/**
 * Shake background
 */
	handleShakeScreen = () => {
		this.setState({shakeScreen:true}, () => {
			if (this.timeout) clearTimeout(this.timeout);
			this.timeout = setTimeout(() => {this.setState({shakeScreen: false});}, 500);
		});
	};

	/**
	 * Render component
	 */
	render = () => {
		/* Get language id (use browser language if user has not selected language) */
		const languageId = (
			this.state.playerData && 
			this.state.playerData.languageId && 
			languagesData.some((l) => {return l.id === this.state.playerData.languageId;})
				? this.state.playerData.languageId
				: this.props.languageId
		);

		return (
			<DndProvider options={HTML5toTouch}>
				<ImageLoader type={'basic-game'} />
				<Game 
					shakeScreen={this.state.shakeScreen}
					languageId={languageId}
					backgroundId={this.state.backgroundId}
					backgroundModifier={this.state.backgroundModifier}
					backgroundStatus={this.state.backgroundStatus}
					playerData={this.state.playerData}
					deviceInfo={this.props.deviceInfo}
					scrollToTop={this.props.scrollToTop}
					updatePlayerData={this.updatePlayerData}
					resetPlayerData={this.resetPlayerData}
					startNewSessionInModule={this.startNewSessionInModule}
					setBackground={this.setBackground}
					updateBackgroundStatus={this.updateBackgroundStatus}
					handleShakeScreen={this.handleShakeScreen}
				/>
			</DndProvider>
		);
	};
};

PlayerController.propTypes = {
	languageId: PropTypes.string.isRequired,
	deviceInfo: PropTypes.object.isRequired,
	scrollToTop: PropTypes.func.isRequired,
};

export default PlayerController;
